import axios from 'axios'
import qs from 'qs'

import store from '@/store'

import { MissingTokenError, TokenExpiredError } from './errors'

const API_URL = process.env.VUE_APP_API_URL

// Attach auth header to outgoing requests
axios.interceptors.request.use(config => {
  const token = store.getters['auth/accessToken']

  if (!token) {
    throw new MissingTokenError()
  }
  if (!store.getters['auth/isAuthenticated']) {
    throw new TokenExpiredError()
  }
  config.headers.Authorization = `Bearer ${token}`

  return config
})

/**
 * Wrapper for axios request method.
 *
 * If a request results in a 401, it's likely
 * that the existing auth session is no longer valid even
 * though the application thinks it is. In this case, we
 * need to clear the session and send the user back to the
 * login page for re-authentication.
 *
 * See https://github.com/axios/axios#request-config
 *
 * @param {Object} config - axios config options
 * @param {Boolean} handleErrors - whether or not we want to automatically handle errors
 * @param {String} [contentType=application/json] - content-type header
 * @returns {Promise}
 */
export const request = async (config, handleErrors, contentType = 'application/json') => {
  const options = {
    baseURL: API_URL,
    headers: {
      'Content-Type': contentType
    },
    paramsSerializer: params => {
      if (params instanceof URLSearchParams) {
        return params
      }
      return qs.stringify(params)
    },
    ...config
  }
  try {
    return await axios.request(options)
  } catch (err) {
    if (handleErrors) {
      store.dispatch('error/show', err)
    } else {
      throw err
    }
  }
}
/**
 * Perform a GET request for the full set of paginated data.
 *
 * @param {Function} request - api endpoint
 * @param {String} id - for endpoints that require id
 * @param {Object} [params] - additional query params
 * @param {Number} [pageSize] - desired page size (25 to 500)
 */
export const paginatedRequest = async (request, id, params = new URLSearchParams(), pageSize = 100) => {
  // Always send query params as URLSearchParams
  if (!(params instanceof URLSearchParams)) {
    const urlSearchParams = new URLSearchParams()

    for (const [key, value] of Object.entries(params)) {
      urlSearchParams.append(key, value)
    }

    params = urlSearchParams
  }
  params.append('page_size', pageSize)

  const config = {
    id: id,
    params
  }
  // Get the total object count from the first response
  const response = await request(config)
  const total = response.data.count
  let data = response.data.results

  // Create and send the remaning requests in parallel
  const numPages = Math.ceil(total / pageSize)
  const promises = []

  for (let page = 2; page <= numPages; page++) {
    const localConfig = {
      id: id,
      params: new URLSearchParams(config.params)
    }
    localConfig.params.set('page', page)
    promises.push(request(localConfig))
  }

  const responses = await Promise.all(promises)

  for (const response of responses) {
    data = data.concat(response.data.results)
  }

  return { data }
}
